import { useTranslation } from '@klv/hooks/useTranslation';
import { debounce } from 'lodash-es';
import React, { Fragment, useEffect, useRef, useState } from 'react';
import { generateClassName } from '@klv/shared/utils/functions';

import { Wrapper } from '@klv/utils/wrapper';
import Button from '@klv/atoms/button';
import Image from '@klv/atoms/image';
import { Rive } from '@klv/atoms/rive';

import { Pills } from './pills';
import * as styles from './styles/solutionsScrollAccordion.module.scss';
import useSolutionContent from './utils/data';


import type { ISolutionsScrollAccordion } from './types';
import type { IGatsbyImageData } from 'gatsby-plugin-image';

export const SolutionsScrollAccordion = ({
  className = '',
  includeSolutionsScrollAccordion,
}: ISolutionsScrollAccordion) => {
  
  const { t } = useTranslation();
  const { rowContent } = useSolutionContent();

  const pillsRefs = useRef<(HTMLDivElement | null)[]>([]);
  const containerRef = useRef<(HTMLDivElement | null)>(null);
  
  const [menuIsVisible, setMenuIsVisible] = useState(false);
  
  const handleScroll = debounce(() => {
    if (pillsRefs.current.length > 0) {
      const firstElement = pillsRefs.current[0];
      const secondElement = pillsRefs.current[1];
      const lastElement = pillsRefs.current[pillsRefs.current.length - 1];

      if (firstElement && lastElement && secondElement) {

        const firstElementRect = firstElement.getBoundingClientRect();
        const secondElementRect = secondElement.getBoundingClientRect();
        const lastElementRect = lastElement.getBoundingClientRect();

        if (lastElementRect.bottom - window.innerHeight <= 0 && !menuIsVisible) {
          setMenuIsVisible(true);

        } else if (firstElementRect.bottom >= secondElementRect.top) {
          setMenuIsVisible(false);
        }
      }
    }
  }, 5);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  });

  if (!includeSolutionsScrollAccordion) return null; 

  return (
    <div>
      <Wrapper className={generateClassName([className, styles.solutionsScrollAccordion])} customPadding={{ customPaddingBottom: 'lg', customPaddingTop: 'xl' }}  backgroundColor='poppy'>
        <div ref={containerRef} className={generateClassName([styles.accordionContainer, menuIsVisible ? styles.open : ''])}>
          {rowContent.map((row, i) => (
            <Fragment key={`${i}-${row.headline}`}>
              <div ref={(el) => (pillsRefs.current[i] = el)} style={{ backgroundColor: 'var(--color-core-poppy)' } as React.CSSProperties} className={generateClassName([ styles.rowHeadline, styles.customGridColumn])}>
                  <h2 className={generateClassName(['display1', 'inlinePadding'])}>{row.headline}</h2>
                  {row.pills.length > 0 && <Pills removePills={menuIsVisible} pills={row.pills.filter((pill): pill is string => pill !== undefined)} />} 
              </div>
              <Wrapper className={generateClassName([styles.customGridColumn])} grid contained={false} inlinePadding={true} customPadding={{ customPaddingBottom: 'xs', customPaddingTop: 'xs' }}  backgroundColor='poppy'>
                <div className={styles.rowText}>
                  <h3>{row.subhead}</h3>
                    <div className={styles.buttonContainer}>
                      <Button
                        text={row.button}
                        url={row.href}
                        setTabIndex={1}
                        buttonColor="dark"
                      />
                    </div>
                    <div className={styles.rowLinks}>
                      <div className={styles.linksContainer}>
                        {row.explore.map((explore, index) => (
                          <a href={explore.href} tabIndex={1} key={`${index}-${explore.href}`}>{explore.button}</a>
                        ))}
                      </div>
                  </div>
                </div>
                <div className={generateClassName([(rowContent.length === i + 1) ? styles.last : '', styles.rowMedia])}>
                  {row.rive ? (
                    <Rive
                      riveFile={{ mediaItemUrl:row.rive.riveFile }}
                      riveText={row.rive.riveText}
                      aspectRatio={row.rive.aspectRatio}
                    />
                  ) : (
                    <Image imageData={row.media.gatsbyImage as IGatsbyImageData} altText={row.media?.altText} />
                  )}
                  
                  
                </div>
              </Wrapper>
            </Fragment>
          ))}
        </div>

        {/* Bottom Section */}
        <Wrapper className={styles.customGridColumn} grid contained={false} inlinePadding={true} customPadding={{ customPaddingBottom: 'xs', customPaddingTop: 'xl' }}  backgroundColor='poppy'>
          <div className={styles.subheadButton}>
            <h3 className={generateClassName(['', styles.mainSubhead])}>{t('organisms_solutionsScrollAccordion_bottom_subhead')}</h3>
            <Button 
              text={t('organisms_solutionsScrollAccordion_bottom_button')}
              url={t('organisms_solutionsScrollAccordion_bottom_href')}
              setTabIndex={1}
              buttonColor="dark"
            />
            </div>
        </Wrapper>
      </Wrapper>
    </div>
  );
};


